import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  featuredInsightSlug,
  featuredInsightSummary,
  getInsightCategories,
  getInsightDisplayTags,
  insightArticles,
  insightCategoryFilters,
  type InsightCategory,
} from '@/lib/insights';

export default function Insights() {
  const [activeFilter, setActiveFilter] = useState<InsightCategory>('All');

  const featuredArticle = insightArticles.find((article) => article.slug === featuredInsightSlug) ?? insightArticles[0];

  const recentArticles = useMemo(
    () => insightArticles.filter((article) => article.slug !== featuredArticle.slug).slice(-6).reverse(),
    [featuredArticle.slug],
  );

  const filteredArticles = useMemo(() => {
    const articles = insightArticles.filter((article) => article.slug !== featuredArticle.slug);

    if (activeFilter === 'All') {
      return articles;
    }

    return articles.filter((article) => getInsightCategories(article).includes(activeFilter));
  }, [activeFilter, featuredArticle.slug]);

  return (
    <MainLayout>
      <SEO
        title="CanXP AI Insights — Sovereign AI, Private AI, and Model Training"
        description="Explore CanXP AI insight articles on sovereign AI, private AI infrastructure, small language models, Canadian AI strategy, and enterprise model training."
        keywords="CanXP AI insights, sovereign AI Canada, private AI articles, AI model training insights, Canadian AI infrastructure"
        url="/insights"
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="container mx-auto max-w-7xl px-4 py-12 md:py-20">
          <section className="mx-auto max-w-4xl text-center">
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              CanXP AI Insights
            </Badge>
            <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
              Sovereign AI Insight Library.
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Our insight library is designed to educate you on how to evaluate sovereign AI solutions.
            </p>
          </section>

          <section className="mx-auto mt-14 max-w-6xl">
            <Card className="border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_18px_80px_-50px_rgba(220,38,38,0.8)]">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge className="bg-primary text-primary-foreground hover:bg-primary">Featured Guide</Badge>
                  {getInsightDisplayTags(featuredArticle).slice(0, 2).map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="mt-6 max-w-4xl">
                  <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">{featuredArticle.title}</h2>
                  <p className="mt-5 text-base leading-8 text-muted-foreground md:text-lg">
                    {featuredInsightSummary}
                  </p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to={featuredArticle.path}>
                      Read featured guide
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="mt-12">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Recently updated</p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight md:text-3xl">Fresh additions to the insight library</h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground md:text-base">
                  Start here to read the latest articles added to the CanXP AI insights collection.
                </p>
              </div>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {recentArticles.map((article) => {
                const displayTags = getInsightDisplayTags(article).slice(0, 2);

                return (
                  <Card
                    key={article.slug}
                    className="h-full border-primary/15 bg-gradient-to-br from-primary/5 via-background to-background shadow-[0_18px_60px_-40px_rgba(220,38,38,0.35)]"
                  >
                    <CardContent className="flex h-full flex-col p-6">
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-primary text-primary-foreground hover:bg-primary">Updated</Badge>
                        {displayTags.map((keyword) => (
                          <Badge key={keyword} variant="secondary">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                      <h3 className="mt-4 text-xl font-semibold tracking-tight">{article.title}</h3>
                      <p className="mt-4 flex-1 text-sm leading-7 text-muted-foreground">{article.metaDescription}</p>
                      <Button asChild variant="link" className="mt-4 px-0 text-primary">
                        <Link to={article.path}>
                          Read latest article
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="mt-12">
            <div className="flex flex-wrap gap-2">
              {insightCategoryFilters.map((filter) => {
                const isActive = filter === activeFilter;

                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setActiveFilter(filter)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground'
                    }`}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">

            {filteredArticles.map((article) => {
              const displayTags = getInsightDisplayTags(article).slice(0, 2);

              return (
                <Card
                  key={article.slug}
                  className="h-full border-border/70 bg-background/80 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.45)]"
                >
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="flex flex-wrap gap-2">
                      {displayTags.map((keyword) => (
                        <Badge key={keyword} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight">{article.title}</h2>
                    <p className="mt-4 flex-1 text-sm leading-7 text-muted-foreground">{article.metaDescription}</p>
                    <Button asChild variant="link" className="mt-4 px-0 text-primary">
                      <Link to={article.path}>
                        Read article
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </section>
        </main>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
